// Fonctions pures du greffe de mails — MIROIR de supabase/functions/_shared/emailitInbound.ts
// (Deno ne peut pas importer le monorepo). Toute modification ici doit être répliquée là-bas.
export const GRAFT_DOMAIN = 'inbound.mail.cime-avocats.com';

export function normalizeEmail(raw: string): string | null {
  const m = raw.match(/<([^>]+)>/);
  const addr = (m ? m[1]! : raw).trim().toLowerCase();
  return addr.lastIndexOf('@') > 0 ? addr : null;
}

export function buildGraftAddress(dossierNumber: string): string {
  return `${dossierNumber.trim().toLowerCase()}@${GRAFT_DOMAIN}`;
}

export function extractGraftLocalParts(
  addresses: readonly (string | null | undefined)[],
): string[] {
  const seen = new Set<string>();
  for (const raw of addresses) {
    if (!raw) continue;
    const addr = normalizeEmail(raw);
    if (!addr) continue;
    const at = addr.lastIndexOf('@');
    if (addr.slice(at + 1) === GRAFT_DOMAIN) seen.add(addr.slice(0, at));
  }
  return [...seen];
}

export function detectDirection(
  toEmails: string[], ccEmails: string[],
): 'inbound_forward' | 'outbound_bcc' {
  return extractGraftLocalParts([...toEmails, ...ccEmails]).length > 0
    ? 'inbound_forward' : 'outbound_bcc';
}

// ── Adresse unique + résolution par le sujet (spec 2026-07-21) ───────────────
// Local part réservé : aucun numéro de dossier (format CIME-…) ne peut le percuter.
export const GRAFT_UNIFIED_LOCAL = 'dossiers';
export const GRAFT_UNIFIED_ADDRESS = `${GRAFT_UNIFIED_LOCAL}@${GRAFT_DOMAIN}`;

export function extractDossierRefsFromSubject(subject: string): string[] {
  // Avec préfixe « cime » : séparateurs libres. Sans préfixe : tiret OBLIGATOIRE,
  // sinon « 2026 07 » (date/montant du sujet d'origine) déclencherait des greffes
  // auto erronées. Bornes (?<!\d)/(?!\d) : jamais au milieu d'un nombre plus long.
  const refs = new Set<string>();
  for (const re of [
    /cime[-\s]?(20\d{2})[-\s]?(\d{1,4})(?!\d)/gi,
    /(?<!\d)(20\d{2})-(\d{1,4})(?!\d)/g,
  ]) {
    for (const m of subject.matchAll(re)) refs.add(`CIME-${m[1]}-${m[2]!.padStart(4, '0')}`);
  }
  return [...refs];
}

// Mots trop génériques dans un intitulé de dossier pour identifier quoi que ce soit.
const TITLE_STOPLIST = new Set([
  'les', 'des', 'sci', 'sarl', 'sas', 'sasu', 'eurl', 'maitre', 'cabinet', 'affaire', 'dossier', 'contre',
]);

export function normalizeForTitleMatch(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ').trim();
}

export function titleTokens(title: string): string[] {
  return [...new Set(normalizeForTitleMatch(title).split(' '))]
    .filter((t) => t.length >= 3 && !/^\d+$/.test(t) && !TITLE_STOPLIST.has(t));
}

export function decodeMimeHeader(raw: string): string {
  // EmailIt renvoie le sujet BRUT : encodé RFC 2047 dès qu'il contient un accent
  // (=?utf-8?Q?...?= ou ?B?...). Sans décodage : affichage illisible et match
  // par titre cassé sur les noms accentués. Deux encoded-words adjacents
  // séparés par du blanc se concatènent sans espace (RFC 2047 §6.2).
  return raw
    .replace(/(=\?[^?]+\?[QqBb]\?[^?]*\?=)\s+(?==\?)/g, '$1')
    .replace(/=\?([^?]+)\?([QqBb])\?([^?]*)\?=/g, (all, charset: string, enc: string, text: string) => {
      try {
        const binary = /^[Bb]$/.test(enc)
          ? atob(text)
          : text.replace(/_/g, ' ').replace(/=([0-9A-Fa-f]{2})/g, (_m, h: string) => String.fromCharCode(parseInt(h, 16)));
        const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
        return new TextDecoder(charset.toLowerCase(), { fatal: true }).decode(bytes);
      } catch {
        return all;
      }
    });
}

export function matchDossiersByTitle(
  subject: string,
  dossiers: readonly { id: string; title: string }[],
): { id: string; score: number }[] {
  const words = new Set(normalizeForTitleMatch(subject).split(' '));
  const out: { id: string; score: number }[] = [];
  for (const d of dossiers) {
    const score = titleTokens(d.title).filter((t) => words.has(t)).length;
    if (score > 0) out.push({ id: d.id, score });
  }
  return out.sort((a, b) => b.score - a.score);
}
